import { inject, Injectable } from '@angular/core';
import { AccountService } from '@infodocs/shared/services/account.service';
import { UserpilotService } from '@infodocs/shared/services/userpilot.service';
import {
	resolveHttpStatus,
	type HttpLikeError,
} from '@infodocs/shared/utils/company-search-errors';

export type CompanyAddedSource = 'company_search' | 'director_search' | 'manual_add';

export type CompanyAddErrorType =
	| 'company_limit'
	| 'validation_error'
	| 'access_denied'
	| 'external_data_error'
	| 'server_error'
	| 'unknown';

export type CompanyAddedJourneyEvent = 'company_added' | 'company_add_failed';

export type CompanyAddSearchLabel = 'Company Search' | 'Director Search';

type CompanyAddedPayload = {
	account_id: number | null;
	occurred_at: string;
	source: CompanyAddedSource;
	company_id: number | null;
	error_type?: CompanyAddErrorType;
	http_status?: number;
};

@Injectable({
	providedIn: 'root',
})
export class CompanyAddedJourneyLoggerService {
	private readonly accountService = inject(AccountService);
	private readonly userpilotService = inject(UserpilotService);
	private readonly firedEvents = new Set<string>();

	public resetSession(): void {
		this.firedEvents.clear();
	}

	public logAdded(source: CompanyAddedSource, companyId: number | null): void {
		this.track('company_added', {
			source,
			company_id: companyId,
		});
	}

	public logFailed(
		source: CompanyAddedSource,
		error: HttpLikeError | null,
		companyId: number | null = null
	): void {
		const httpStatus = resolveHttpStatus(error);
		const fields: Omit<CompanyAddedPayload, 'account_id' | 'occurred_at'> = {
			source,
			company_id: companyId,
			error_type: resolveErrorType(httpStatus),
		};

		if (httpStatus !== undefined) {
			fields.http_status = httpStatus;
		}

		this.track('company_add_failed', fields);
	}

	public sourceFromSearchOption(searchOption: CompanyAddSearchLabel): CompanyAddedSource {
		return searchOption === 'Director Search' ? 'director_search' : 'company_search';
	}

	private track(
		eventName: CompanyAddedJourneyEvent,
		fields: Omit<CompanyAddedPayload, 'account_id' | 'occurred_at'>
	): void {
		const payload: CompanyAddedPayload = {
			account_id: this.accountService.currentAccount()?.id ?? null,
			occurred_at: new Date().toISOString(),
			...fields,
		};

		const dedupeKey = JSON.stringify({
			eventName,
			account_id: payload.account_id,
			company_id: payload.company_id,
			source: payload.source,
			error_type: payload.error_type,
			http_status: payload.http_status,
		});

		if (this.firedEvents.has(dedupeKey)) {
			return;
		}

		this.firedEvents.add(dedupeKey);
		this.userpilotService.track(eventName, payload);
	}
}

export function resolveErrorType(httpStatus?: number): CompanyAddErrorType {
	if (httpStatus === 403) {
		return 'company_limit';
	}

	if (httpStatus === 401) {
		return 'access_denied';
	}

	if (httpStatus === 422 || httpStatus === 400) {
		return 'validation_error';
	}

	if (httpStatus === 404 || httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
		return 'external_data_error';
	}

	if (httpStatus !== undefined && httpStatus >= 500) {
		return 'server_error';
	}

	return 'unknown';
}
